import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  Filter,
  LayoutGrid,
  Loader2,
  MessageSquare,
  Paperclip,
  Pencil,
  Phone,
  Plus,
  Search,
  Send,
  Settings,
  Table2,
  Trash2,
  UserCircle2,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { DateInput } from "../DateInput";
import { useAuth } from "../AuthContext";
import { isAdminRole } from "../../lib/roles";
import { formatCnpj, formatPhone } from "../../lib/masks";
import {
  Card,
  EmptyState,
  PageHeader,
  formatDate,
  formatDateTime,
} from "../ui";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const STAGE_COLOR = {
  novo_lead: "bg-slate-100 text-slate-700 border-slate-300",
  em_analise: "bg-indigo-50 text-indigo-700 border-indigo-300",
  contato_pendente: "bg-sky-50 text-sky-700 border-sky-300",
  primeiro_contato: "bg-cyan-50 text-cyan-700 border-cyan-300",
  em_negociacao: "bg-amber-50 text-amber-800 border-amber-300",
  proposta_enviada: "bg-orange-50 text-orange-800 border-orange-300",
  aguardando_resposta: "bg-yellow-50 text-yellow-800 border-yellow-300",
  associacao_aprovada: "bg-emerald-50 text-emerald-800 border-emerald-300",
  convertido: "bg-[#002776] text-white border-[#002776]",
  perdido: "bg-rose-50 text-rose-700 border-rose-300",
};

const ACTIVITY_LABEL = {
  ligacao: "Ligação",
  whatsapp: "WhatsApp",
  email: "E-mail",
  reuniao: "Reunião",
  proposta: "Proposta",
  comentario: "Comentário",
  alteracao_estagio: "Alteração de estágio",
  outro: "Outro",
};

const ACTIVITY_ICON = {
  ligacao: Phone,
  whatsapp: MessageSquare,
  email: Send,
  reuniao: UserCircle2,
  proposta: FileText,
  comentario: MessageSquare,
  alteracao_estagio: Pencil,
  outro: MessageSquare,
};

const ORIGINS = [
  "Diretoria",
  "Conselho",
  "Administração",
  "Evento",
  "Indicação Externa",
  "Outro",
];

const StageBadge = ({ stage, label }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[11px] uppercase tracking-wide ${
      STAGE_COLOR[stage] || "bg-slate-100 text-slate-700 border-slate-300"
    }`}
  >
    {label}
  </span>
);

const basePath = (user) =>
  isAdminRole(user?.role) ? "/admin/comercial" : "/portal/comercial";

// ===========================================================================
// LISTAGEM (tabela com filtros)
// ===========================================================================

export const ComercialLeadsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminRole(user?.role);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stages, setStages] = useState([]);
  const [segments, setSegments] = useState([]);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [segFilter, setSegFilter] = useState("");
  const [origemFilter, setOrigemFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      if (q.trim()) params.q = q.trim();
      if (stageFilter) params.stage = stageFilter;
      if (segFilter) params.segmento = segFilter;
      if (origemFilter) params.origem = origemFilter;
      const { data } = await api.get("/comercial/leads", { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, stageFilter, segFilter, origemFilter]);

  useEffect(() => {
    (async () => {
      try {
        const [s, sg] = await Promise.all([
          api.get("/comercial/stages"),
          api.get("/comercial/segments"),
        ]);
        setStages(s.data.items || []);
        setSegments(sg.data.items || []);
      } catch (err) {
        // Non-blocking: filtros auxiliares (stages/segments) ficam vazios
        console.warn("[Comercial] falha ao carregar stages/segments:", err);
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stageLabel = useMemo(() => {
    const m = {};
    stages.forEach((s) => (m[s.key] = s.label));
    return m;
  }, [stages]);

  const doExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/comercial/export", { responseType: "blob" });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Leads-Comercial-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="comercial-leads-list" className="p-6 lg:p-10">
      <PageHeader
        title="Comercial · Leads"
        subtitle="Gestão de potenciais associados indicados pela Diretoria, Conselho e Administração."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded border border-slate-300 bg-white overflow-hidden">
              <Link
                to={`${basePath(user)}/dashboard`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Dashboard
              </Link>
              <span className="px-3 py-2 text-sm bg-[#002776] text-white inline-flex items-center gap-2">
                <Table2 size={14} /> Tabela
              </span>
              <Link
                to={`${basePath(user)}/kanban`}
                data-testid="btn-view-kanban"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <LayoutGrid size={14} /> Kanban
              </Link>
            </div>
            {isAdmin && (
              <>
                <Link
                  to={`${basePath(user)}/segmentos`}
                  className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm rounded hover:bg-slate-50"
                  data-testid="btn-segments"
                >
                  <Settings size={14} /> Segmentos
                </Link>
                <button
                  onClick={doExport}
                  disabled={exporting}
                  data-testid="btn-export"
                  className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  Exportar XLSX
                </button>
              </>
            )}
            <Link
              to={`${basePath(user)}/leads/novo`}
              data-testid="btn-new-lead"
              className="inline-flex items-center gap-2 bg-[#002776] text-white px-4 py-2 text-sm rounded font-semibold hover:brightness-110"
            >
              <Plus size={16} /> Indicar novo associado
            </Link>
          </div>
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por empresa, CNPJ ou contato..."
              data-testid="input-search"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30"
            />
          </div>
          <button
            onClick={() => setShowFilters((s) => !s)}
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm rounded hover:bg-slate-50"
            data-testid="btn-toggle-filters"
          >
            <Filter size={14} /> Filtros
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="filter-stage"
            >
              <option value="">Todos os estágios</option>
              {stages.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <select
              value={segFilter}
              onChange={(e) => setSegFilter(e.target.value)}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="filter-segment"
            >
              <option value="">Todos os segmentos</option>
              {segments.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              value={origemFilter}
              onChange={(e) => setOrigemFilter(e.target.value)}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="filter-origin"
            >
              <option value="">Todas as origens</option>
              {ORIGINS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        )}
      </Card>

      {loading ? (
        <div className="py-14 text-center text-slate-500">
          <Loader2 className="inline animate-spin mr-2" size={16} /> Carregando leads...
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nenhum lead encontrado"
          hint="Clique em “Indicar novo associado” para começar a construir o funil."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Empresa</th>
                  <th className="text-left px-4 py-3 font-medium">Segmento</th>
                  <th className="text-left px-4 py-3 font-medium">Contato</th>
                  <th className="text-left px-4 py-3 font-medium">Indicado por</th>
                  <th className="text-left px-4 py-3 font-medium">Estágio</th>
                  <th className="text-left px-4 py-3 font-medium">Última atividade</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() =>
                      navigate(`${basePath(user)}/leads/${r.id}`)
                    }
                    data-testid={`row-lead-${r.id}`}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">
                        {r.razao_social}
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.nome_fantasia || "—"}
                        {r.cnpj ? ` · CNPJ ${formatCnpj(r.cnpj)}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.segmento || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-800">
                        {r.contato_nome || "—"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.contato_email || formatPhone(r.contato_telefone) || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.indicated_by_name || "—"}
                      <div className="text-xs text-slate-500">
                        {formatDate(r.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StageBadge
                        stage={r.stage}
                        label={stageLabel[r.stage] || r.stage}
                      />
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {formatDateTime(r.last_activity_at || r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

// ===========================================================================
// FORMULÁRIO DE NOVO LEAD
// ===========================================================================

const emptyLead = {
  razao_social: "",
  nome_fantasia: "",
  cnpj: "",
  segmento: "",
  site: "",
  cidade: "",
  estado: "",
  pais: "Brasil",
  descricao: "",
  contato_nome: "",
  contato_cargo: "",
  contato_email: "",
  contato_telefone: "",
  contato_linkedin: "",
  origem: "Diretoria",
  justificativa: "",
  observacoes: "",
};

export const ComercialNovoLead = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyLead);
  const [segments, setSegments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/comercial/segments").then((r) => setSegments(r.data.items || []));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.razao_social.trim().length < 2) {
      toast.error("Informe a Razão Social");
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
      );
      // Clean optional empties → omit
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      const { data } = await api.post("/comercial/leads", payload);
      toast.success("Lead criado com sucesso");
      navigate(`${basePath(user)}/leads/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, children, required, span = "" }) => (
    <label className={`flex flex-col gap-1 ${span}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
    </label>
  );

  const input =
    "border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30 bg-white";

  return (
    <div data-testid="comercial-novo-lead" className="p-6 lg:p-10 max-w-5xl">
      <PageHeader
        title="Indicar novo associado"
        subtitle="Compartilhe os dados do potencial associado. A administração entrará em contato comercial a partir daqui."
        actions={
          <Link
            to={`${basePath(user)}/leads`}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Voltar
          </Link>
        }
      />

      <form onSubmit={onSubmit} className="space-y-6">
        <Card className="p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 size={16} className="text-[#002776]" /> Dados da empresa
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Razão Social" required span="md:col-span-2">
              <input
                data-testid="input-razao-social"
                value={form.razao_social}
                onChange={(e) => update("razao_social", e.target.value)}
                className={input}
                maxLength={200}
                required
              />
            </Field>
            <Field label="Nome Fantasia">
              <input
                value={form.nome_fantasia}
                onChange={(e) => update("nome_fantasia", e.target.value)}
                className={input}
              />
            </Field>
            <Field label="CNPJ">
              <input
                data-testid="input-cnpj"
                value={formatCnpj(form.cnpj)}
                onChange={(e) =>
                  update("cnpj", e.target.value.replace(/\D/g, "").slice(0, 14))
                }
                placeholder="00.000.000/0000-00"
                className={input}
              />
            </Field>
            <Field label="Segmento de atuação">
              <select
                value={form.segmento}
                onChange={(e) => update("segmento", e.target.value)}
                className={input}
                data-testid="input-segmento"
              >
                <option value="">Selecione...</option>
                {segments.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Site">
              <input
                type="url"
                value={form.site}
                onChange={(e) => update("site", e.target.value)}
                placeholder="https://..."
                className={input}
              />
            </Field>
            <Field label="Cidade">
              <input
                value={form.cidade}
                onChange={(e) => update("cidade", e.target.value)}
                className={input}
              />
            </Field>
            <Field label="UF">
              <input
                value={form.estado}
                onChange={(e) =>
                  update("estado", e.target.value.toUpperCase().slice(0, 2))
                }
                maxLength={2}
                className={input}
              />
            </Field>
            <Field label="País">
              <input
                value={form.pais}
                onChange={(e) => update("pais", e.target.value)}
                className={input}
              />
            </Field>
            <Field label="Descrição da empresa" span="md:col-span-2">
              <textarea
                rows={3}
                value={form.descricao}
                onChange={(e) => update("descricao", e.target.value)}
                className={input}
              />
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <UserCircle2 size={16} className="text-[#002776]" /> Contato principal
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome do contato">
              <input
                value={form.contato_nome}
                onChange={(e) => update("contato_nome", e.target.value)}
                className={input}
                data-testid="input-contato-nome"
              />
            </Field>
            <Field label="Cargo">
              <input
                value={form.contato_cargo}
                onChange={(e) => update("contato_cargo", e.target.value)}
                className={input}
              />
            </Field>
            <Field label="E-mail">
              <input
                type="email"
                value={form.contato_email}
                onChange={(e) => update("contato_email", e.target.value)}
                className={input}
              />
            </Field>
            <Field label="Telefone / WhatsApp">
              <input
                value={formatPhone(form.contato_telefone)}
                onChange={(e) =>
                  update(
                    "contato_telefone",
                    e.target.value.replace(/\D/g, "").slice(0, 11)
                  )
                }
                className={input}
              />
            </Field>
            <Field label="LinkedIn" span="md:col-span-2">
              <input
                type="url"
                value={form.contato_linkedin}
                onChange={(e) => update("contato_linkedin", e.target.value)}
                placeholder="https://linkedin.com/in/..."
                className={input}
              />
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Pencil size={16} className="text-[#002776]" /> Indicação
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Origem">
              <select
                value={form.origem}
                onChange={(e) => update("origem", e.target.value)}
                className={input}
                data-testid="input-origem"
              >
                {ORIGINS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Justificativa / Potencial de associação" span="md:col-span-2">
              <textarea
                rows={3}
                value={form.justificativa}
                onChange={(e) => update("justificativa", e.target.value)}
                className={input}
              />
            </Field>
            <Field label="Observações adicionais" span="md:col-span-2">
              <textarea
                rows={2}
                value={form.observacoes}
                onChange={(e) => update("observacoes", e.target.value)}
                className={input}
              />
            </Field>
          </div>
        </Card>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`${basePath(user)}/leads`)}
            className="px-4 py-2 text-sm rounded border border-slate-300 bg-white hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="btn-save-lead"
            className="inline-flex items-center gap-2 bg-[#002776] text-white px-5 py-2 text-sm rounded font-semibold hover:brightness-110 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Cadastrar lead
          </button>
        </div>
      </form>
    </div>
  );
};

// ===========================================================================
// DETALHE DO LEAD com 4 abas
// ===========================================================================

const TABS = [
  { key: "info", label: "Informações" },
  { key: "atividades", label: "Histórico" },
  { key: "acoes", label: "Próximas ações" },
  { key: "docs", label: "Documentos" },
];

export const ComercialLeadDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminRole(user?.role);
  const [tab, setTab] = useState("info");
  const [lead, setLead] = useState(null);
  const [stages, setStages] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingStage, setSavingStage] = useState(false);

  const stageLabel = useMemo(() => {
    const m = {};
    stages.forEach((s) => (m[s.key] = s.label));
    return m;
  }, [stages]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s, r] = await Promise.all([
        api.get(`/comercial/leads/${id}`),
        api.get("/comercial/stages"),
        api.get("/comercial/users/responsaveis"),
      ]);
      setLead(l.data);
      setStages(s.data.items || []);
      setResponsaveis(r.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      navigate(`${basePath(user)}/leads`);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, user]);

  useEffect(() => {
    reload();
  }, [reload]);

  const changeStage = async (newStage) => {
    if (!newStage || newStage === lead.stage) return;
    setSavingStage(true);
    try {
      const { data } = await api.post(`/comercial/leads/${id}/stage`, {
        stage: newStage,
      });
      setLead(data);
      toast.success("Estágio atualizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingStage(false);
    }
  };

  const assignResp = async (userId) => {
    try {
      const { data } = await api.patch(`/comercial/leads/${id}`, {
        assigned_to_id: userId || null,
      });
      setLead(data);
      toast.success("Responsável atualizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  if (loading || !lead) {
    return (
      <div className="p-10 text-center text-slate-500">
        <Loader2 className="inline animate-spin mr-2" size={16} /> Carregando...
      </div>
    );
  }

  return (
    <div data-testid="comercial-lead-detail" className="p-6 lg:p-10 max-w-6xl">
      <div className="mb-4">
        <Link
          to={`${basePath(user)}/leads`}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={14} /> Voltar aos leads
        </Link>
      </div>

      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif-display text-3xl text-slate-900">
              {lead.razao_social}
            </h1>
            <div className="text-sm text-slate-600 mt-1">
              {lead.nome_fantasia ? `${lead.nome_fantasia} · ` : ""}
              {lead.cnpj ? `CNPJ ${formatCnpj(lead.cnpj)}` : ""}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StageBadge
                stage={lead.stage}
                label={stageLabel[lead.stage] || lead.stage}
              />
              <span className="text-xs text-slate-500">
                Indicado por <strong>{lead.indicated_by_name}</strong> em{" "}
                {formatDate(lead.created_at)}
              </span>
            </div>
          </div>

          {isAdmin && (
            <div className="flex flex-col md:items-end gap-2">
              <label className="text-xs text-slate-600">Mover para estágio</label>
              <select
                value={lead.stage}
                onChange={(e) => changeStage(e.target.value)}
                disabled={savingStage}
                data-testid="select-stage"
                className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
              >
                {stages.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
              <label className="text-xs text-slate-600 mt-2">Responsável comercial</label>
              <select
                value={lead.assigned_to_id || ""}
                onChange={(e) => assignResp(e.target.value)}
                className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
                data-testid="select-assigned"
              >
                <option value="">— sem atribuição —</option>
                {responsaveis.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name || r.email}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Card>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.key
                ? "border-[#002776] text-[#002776]"
                : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && <LeadInfoTab lead={lead} />}
      {tab === "atividades" && (
        <LeadActivitiesTab leadId={id} isAdmin={isAdmin} />
      )}
      {tab === "acoes" && (
        <LeadActionsTab
          leadId={id}
          isAdmin={isAdmin}
          responsaveis={responsaveis}
        />
      )}
      {tab === "docs" && <LeadDocumentsTab leadId={id} isAdmin={isAdmin} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 1 — Informações
// ---------------------------------------------------------------------------

const Row = ({ label, value, span = "" }) => (
  <div className={span}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
      {label}
    </div>
    <div className="text-sm text-slate-800 mt-1">{value || "—"}</div>
  </div>
);

const LeadInfoTab = ({ lead }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <Card className="p-5">
      <h3 className="font-semibold text-slate-900 mb-4">Dados da empresa</h3>
      <div className="grid grid-cols-2 gap-4">
        <Row label="Razão Social" value={lead.razao_social} span="col-span-2" />
        <Row label="Nome Fantasia" value={lead.nome_fantasia} />
        <Row label="CNPJ" value={lead.cnpj ? formatCnpj(lead.cnpj) : null} />
        <Row label="Segmento" value={lead.segmento} />
        <Row
          label="Site"
          value={
            lead.site ? (
              <a
                href={lead.site}
                target="_blank"
                rel="noreferrer"
                className="text-[#002776] underline"
              >
                {lead.site}
              </a>
            ) : null
          }
        />
        <Row label="Cidade" value={lead.cidade} />
        <Row label="UF" value={lead.estado} />
        <Row label="País" value={lead.pais} />
        <Row label="Descrição" value={lead.descricao} span="col-span-2" />
      </div>
    </Card>
    <Card className="p-5">
      <h3 className="font-semibold text-slate-900 mb-4">Contato principal</h3>
      <div className="grid grid-cols-2 gap-4">
        <Row label="Nome" value={lead.contato_nome} span="col-span-2" />
        <Row label="Cargo" value={lead.contato_cargo} />
        <Row label="E-mail" value={lead.contato_email} />
        <Row
          label="Telefone"
          value={
            lead.contato_telefone ? formatPhone(lead.contato_telefone) : null
          }
        />
        <Row
          label="LinkedIn"
          value={
            lead.contato_linkedin ? (
              <a
                href={lead.contato_linkedin}
                target="_blank"
                rel="noreferrer"
                className="text-[#002776] underline"
              >
                abrir
              </a>
            ) : null
          }
        />
      </div>
    </Card>
    <Card className="p-5 lg:col-span-2">
      <h3 className="font-semibold text-slate-900 mb-4">Indicação</h3>
      <div className="grid grid-cols-2 gap-4">
        <Row label="Origem" value={lead.origem} />
        <Row label="Indicado por" value={lead.indicated_by_name} />
        <Row label="Justificativa" value={lead.justificativa} span="col-span-2" />
        <Row label="Observações" value={lead.observacoes} span="col-span-2" />
        {lead.internal_notes && (
          <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1">
              Notas internas (apenas administração)
            </div>
            <div className="text-sm text-amber-900 whitespace-pre-wrap">
              {lead.internal_notes}
            </div>
          </div>
        )}
      </div>
    </Card>
  </div>
);

// ---------------------------------------------------------------------------
// Tab 2 — Histórico de Atividades
// ---------------------------------------------------------------------------

const LeadActivitiesTab = ({ leadId, isAdmin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    type: "comentario",
    description: "",
    is_internal: false,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comercial/leads/${leadId}/activities`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return;
    setSaving(true);
    try {
      await api.post(`/comercial/leads/${leadId}/activities`, form);
      setForm((f) => ({ ...f, description: "" }));
      await load();
      toast.success("Registro adicionado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
              disabled={!isAdmin}
              data-testid="activity-type"
            >
              {Object.entries(ACTIVITY_LABEL)
                .filter(([k]) =>
                  isAdmin ? k !== "alteracao_estagio" : k === "comentario"
                )
                .map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
            </select>
            {isAdmin && (
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_internal}
                  onChange={(e) =>
                    setForm({ ...form, is_internal: e.target.checked })
                  }
                  data-testid="activity-internal"
                />
                Marcar como <strong>interna</strong> (não compartilhar com Diretoria/Conselho)
              </label>
            )}
          </div>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descreva a interação..."
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
            data-testid="activity-description"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || !form.description.trim()}
              data-testid="btn-save-activity"
              className="inline-flex items-center gap-2 bg-[#002776] text-white px-4 py-2 text-sm rounded font-semibold hover:brightness-110 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
              Registrar
            </button>
          </div>
        </form>
      </Card>

      {loading ? (
        <div className="text-center py-6 text-slate-500">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Sem registros ainda" />
      ) : (
        <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4">
          {items.map((a) => {
            const Icon = ACTIVITY_ICON[a.type] || MessageSquare;
            return (
              <li key={a.id} className="pl-6 relative">
                <span className="absolute -left-[11px] top-1 h-5 w-5 rounded-full bg-white border-2 border-[#002776] flex items-center justify-center">
                  <Icon size={10} className="text-[#002776]" />
                </span>
                <Card className="p-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs text-slate-600">
                      <strong className="text-slate-900">
                        {ACTIVITY_LABEL[a.type] || a.type}
                      </strong>{" "}
                      · {a.user_name} · {formatDateTime(a.created_at)}
                    </div>
                    {a.is_internal && (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                        Interna
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">
                    {a.description}
                  </div>
                </Card>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3 — Próximas Ações
// ---------------------------------------------------------------------------

const LeadActionsTab = ({ leadId, isAdmin, responsaveis }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    type: "Ligação",
    description: "",
    due_date: "",
    assigned_to_id: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comercial/leads/${leadId}/actions`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.due_date) delete payload.due_date;
      if (!payload.assigned_to_id) delete payload.assigned_to_id;
      await api.post(`/comercial/leads/${leadId}/actions`, payload);
      setForm({
        type: "Ligação",
        description: "",
        due_date: "",
        assigned_to_id: "",
      });
      await load();
      toast.success("Ação cadastrada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (actionId, status) => {
    try {
      await api.patch(`/comercial/leads/${leadId}/actions/${actionId}`, {
        status,
      });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const statusChip = (s) => {
    const map = {
      pendente: "bg-amber-50 text-amber-800 border-amber-300",
      concluida: "bg-emerald-50 text-emerald-800 border-emerald-300",
      atrasada: "bg-rose-50 text-rose-700 border-rose-300",
    };
    return map[s] || "bg-slate-100 text-slate-700 border-slate-300";
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <Card className="p-5">
          <form
            onSubmit={submit}
            className="grid grid-cols-1 md:grid-cols-6 gap-3"
          >
            <input
              type="text"
              placeholder="Tipo (ex: Ligação)"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="md:col-span-1 border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="action-type"
            />
            <input
              type="text"
              placeholder="Descrição"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="md:col-span-2 border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="action-description"
            />
            <DateInput
              value={form.due_date}
              onChange={(v) => setForm({ ...form, due_date: v })}
              testid="action-due"
              className="min-w-[150px]"
            />
            <select
              value={form.assigned_to_id}
              onChange={(e) =>
                setForm({ ...form, assigned_to_id: e.target.value })
              }
              className="border border-slate-300 rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">Responsável...</option>
              {responsaveis.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name || r.email}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={saving || !form.description.trim()}
              data-testid="btn-save-action"
              className="bg-[#002776] text-white px-4 py-2 text-sm rounded font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Adicionar
            </button>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-6 text-slate-500">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Sem ações cadastradas" />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Ação</th>
                <th className="text-left px-4 py-3 font-medium">Prazo</th>
                <th className="text-left px-4 py-3 font-medium">Responsável</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{a.type}</div>
                    <div className="text-xs text-slate-600">{a.description}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatDate(a.due_date)}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {a.assigned_to_name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] uppercase ${statusChip(
                        a.status
                      )}`}
                    >
                      {a.status}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {a.status !== "concluida" && (
                        <button
                          onClick={() => setStatus(a.id, "concluida")}
                          data-testid={`btn-complete-${a.id}`}
                          className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1"
                        >
                          <CheckCircle2 size={12} /> Concluir
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 4 — Documentos
// ---------------------------------------------------------------------------

const LeadDocumentsTab = ({ leadId, isAdmin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comercial/leads/${leadId}/documents`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) {
      toast.error("Informe título e arquivo");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("file", file);
      await api.post(`/comercial/leads/${leadId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTitle("");
      setFile(null);
      await load();
      toast.success("Documento enviado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const resp = await api.get(
        `/comercial/leads/${leadId}/documents/${doc.id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], {
        type: doc.content_type || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || doc.title;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Excluir documento "${doc.title}"?`)) return;
    try {
      await api.delete(`/comercial/leads/${leadId}/documents/${doc.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <Card className="p-5">
          <form
            onSubmit={submit}
            className="grid grid-cols-1 md:grid-cols-5 gap-3"
          >
            <input
              type="text"
              placeholder="Título (ex: Proposta Comercial Abril/2026)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="md:col-span-2 border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="doc-title"
            />
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="md:col-span-2 text-sm"
              data-testid="doc-file"
            />
            <button
              type="submit"
              disabled={uploading || !file || !title.trim()}
              data-testid="btn-upload-doc"
              className="bg-[#002776] text-white px-4 py-2 text-sm rounded font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {uploading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Paperclip size={14} />
              )}
              Enviar
            </button>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-6 text-slate-500">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Nenhum documento anexado" />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Título</th>
                <th className="text-left px-4 py-3 font-medium">Arquivo</th>
                <th className="text-left px-4 py-3 font-medium">Enviado por</th>
                <th className="text-left px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {d.title}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <FileText className="inline mr-1" size={14} />
                    {d.filename}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {d.uploaded_by_name}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatDateTime(d.uploaded_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => download(d)}
                        className="text-xs text-[#002776] hover:underline inline-flex items-center gap-1"
                        data-testid={`btn-download-doc-${d.id}`}
                      >
                        <Download size={12} /> baixar
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => remove(d)}
                          className="text-xs text-rose-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`btn-delete-doc-${d.id}`}
                        >
                          <Trash2 size={12} /> excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// ===========================================================================
// GESTÃO DE SEGMENTOS (admin)
// ===========================================================================

export const ComercialSegmentos = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comercial/segments");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/comercial/segments", { name: name.trim() });
      setName("");
      await load();
      toast.success("Segmento cadastrado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (seg) => {
    if (!window.confirm(`Excluir segmento "${seg.name}"?`)) return;
    try {
      await api.delete(`/comercial/segments/${seg.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="comercial-segmentos" className="p-6 lg:p-10 max-w-3xl">
      <PageHeader
        title="Segmentos de atuação"
        subtitle="Lista configurável de segmentos para classificar os leads do funil comercial."
        actions={
          <Link
            to={`${basePath(user)}/leads`}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Voltar
          </Link>
        }
      />

      <Card className="p-5 mb-4">
        <form onSubmit={create} className="flex gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Novo segmento..."
            maxLength={120}
            data-testid="input-segment"
            className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={saving || !name.trim()}
            data-testid="btn-create-segment"
            className="bg-[#002776] text-white px-4 py-2 text-sm rounded font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Adicionar
          </button>
        </form>
      </Card>

      {loading ? (
        <div className="text-center py-6 text-slate-500">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Nenhum segmento cadastrado" />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-slate-100 first:border-t-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => remove(s)}
                      data-testid={`btn-delete-seg-${s.id}`}
                      className="text-xs text-rose-700 hover:underline inline-flex items-center gap-1"
                    >
                      <X size={12} /> remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};
